import { useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/DateInput";
import { useAsyncData } from "@/lib/useAsyncData";
import Pagination from "@/components/Pagination";
import { ReportError, ReportLoading } from "@/components/leasing/ReportState";
import { fmtNumber, fmtDate } from "@/lib/format";
import { Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

export default function ReportL2({ financer }) {
  const [dateIn, setDateIn] = useState("2018-01-01");
  const [dateOut, setDateOut] = useState("2030-12-31");
  const [splitYear, setSplitYear] = useState(2018);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { loading, error, data, reload } = useAsyncData(async () => {
    const res = await api.get(`/leasing/reports/l2`, {
      params: { financer, date_in: dateIn, date_out: dateOut, split_year: splitYear, search, page, page_size: pageSize },
    });
    return res.data;
  }, [financer, dateIn, dateOut, splitYear, search, page]);

  const doExport = async () => {
    try {
      await downloadFile(
        `/leasing/reports/l2/export?financer=${financer}&date_in=${dateIn}&date_out=${dateOut}&split_year=${splitYear}`,
        `L2_${financer}.xlsx`
      );
    } catch (e) {
      toast.error("Export eșuat");
    }
  };

  const cols = [
    ["contract", "Contract", "l"],
    ["B", "B Capital", "n"], ["C", "C Dobândă", "n"],
    ["F", "F Princ. an curent", "n"], ["G", "G Dob. an curent", "n"],
    ["H", "H Princ. ani urm.", "n"], ["I", "I Dob. ani urm.", "n"],
    ["J", "J Dif", "n"], ["M", "M Start", "d"],
    ["O", "O Principal", "n"], ["P", "P Dobândă", "n"], ["Q", "Q Val. rămasă", "n"],
    ["date_in_2", "Data în 2", "d"], ["T", "T Principal", "n"], ["U", "U Dobândă", "n"],
    ["V", "V Verif", "c"], ["W", "W Verif", "c"],
  ];

  const rows = data?.rows || [];
  const summary = data?.summary;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Data în</Label>
          <DateInput value={dateIn} onChange={setDateIn} className="w-40" data-testid="l2-date-in" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Data ieșire</Label>
          <DateInput value={dateOut} onChange={setDateOut} className="w-40" data-testid="l2-date-out" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">An de separare</Label>
          <Input type="number" value={splitYear} onChange={(e) => { setSplitYear(parseInt(e.target.value, 10) || 2018); setPage(1); }} className="w-28" data-testid="l2-split-year" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Caută contract</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="l2-search-input" placeholder="Contract…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-8 w-48" />
          </div>
        </div>
        {loading && <span className="flex items-center gap-1 text-xs text-slate-400 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> se calculează…</span>}
        <Button onClick={doExport} variant="outline" className="gap-2 ml-auto" data-testid="l2-export-btn">
          <Download className="h-4 w-4" /> Export Excel
        </Button>
      </div>

      {summary && !error && (
        <div className="flex flex-wrap gap-4 text-sm bg-slate-900 text-white rounded p-3" data-testid="l2-summary">
          <span>Principal (ΣT): <b className="tabular">{fmtNumber(summary.principal)}</b></span>
          <span>Dobândă (ΣU): <b className="tabular">{fmtNumber(summary.interest)}</b></span>
          <span>Total: <b className="tabular text-[#FFC800]">{fmtNumber(summary.total)}</b></span>
          <span className={summary.sumV !== 0 ? "text-red-300" : ""}>ΣV: <b className="tabular">{fmtNumber(summary.sumV)}</b></span>
          <span className={summary.sumW !== 0 ? "text-red-300" : ""}>ΣW: <b className="tabular">{fmtNumber(summary.sumW)}</b></span>
        </div>
      )}

      {error ? (
        <ReportError onRetry={reload} />
      ) : loading && !data ? (
        <ReportLoading />
      ) : (
        <>
        <div className="border rounded-md overflow-auto max-h-[62vh]">
          <table className="text-xs jh-table" data-testid="report-l2-table">
            <thead className="sticky top-0 bg-slate-100">
              <tr>
                {cols.map((c) => (
                  <th key={c[0]} className={`px-3 py-2 whitespace-nowrap ${c[2] === "l" ? "text-left sticky left-0 bg-slate-100 z-10" : "text-right"}`}>{c[1]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-amber-50/40">
                  {cols.map((c) => {
                    const v = r[c[0]];
                    if (c[2] === "l") return <td key={c[0]} className="px-3 py-1.5 font-medium sticky left-0 bg-white z-10 whitespace-nowrap">{v}</td>;
                    if (c[2] === "d") return <td key={c[0]} className="px-3 py-1.5 text-right tabular">{fmtDate(v)}</td>;
                    if (c[2] === "c") return <td key={c[0]} className={`px-3 py-1.5 text-right tabular ${v !== 0 ? "bg-red-100 text-red-700 font-semibold" : ""}`}>{fmtNumber(v)}</td>;
                    return <td key={c[0]} className="px-3 py-1.5 text-right tabular">{fmtNumber(v)}</td>;
                  })}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td className="px-4 py-10 text-center text-slate-400">Niciun contract.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} pageSize={pageSize} total={data?.total || 0} onPage={setPage} label="contracte" testid="l2-pagination" />
        </>
      )}
    </div>
  );
}
